import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { vehicles } from '../data/vehicles';

const { FiPlus, FiX, FiCheck, FiInfo, FiTrendingUp, FiZap, FiActivity } = FiIcons;

const ModelComparison = ({ initialModels = [] }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [showSelector, setShowSelector] = useState(false);

  useEffect(() => {
    if (initialModels.length > 0) {
      const ids = initialModels
        .map(name => vehicles.find(v => v.model.toLowerCase() === name.toLowerCase())?.id)
        .filter(id => id !== undefined);
      setSelectedIds(ids.slice(0, 3));
    } else if (selectedIds.length === 0) {
      // Default selection if none provided
      setSelectedIds([1, 4]); // Q5 and Q7
    }
  }, []);

  const selectedVehicles = selectedIds.map(id => vehicles.find(v => v.id === id));

  const toggleVehicle = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(vId => vId !== id));
    } else if (selectedIds.length < 3) {
      setSelectedIds([...selectedIds, id]);
    } else {
      alert("You can compare up to 3 models at once.");
    }
  };

  const comparisonFeatures = [
    { label: 'Starting MSRP', key: 'price', format: (val) => `$${val.toLocaleString()}`, icon: FiTrendingUp },
    { label: 'Engine', key: 'engine', format: (val) => val, icon: FiZap },
    { label: 'Transmission', key: 'transmission', format: (val) => val, icon: FiActivity },
    { label: 'Efficiency', key: 'mpg', format: (val) => `${val} MPG`, icon: FiInfo },
    { label: 'Mileage', key: 'mileage', format: (val) => `${val.toLocaleString()} mi`, icon: FiActivity },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-2xl"
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-700 bg-gray-800/50 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-white">Model Comparison</h3>
          <p className="text-xs text-gray-400">Compare specifications and features</p>
        </div>
        <button
          onClick={() => setShowSelector(!showSelector)}
          className="flex items-center space-x-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded-lg text-sm transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Model</span>
        </button>
      </div>

      {/* Model Selector Modal-ish */}
      <AnimatePresence>
        {showSelector && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-gray-900/50 border-b border-gray-700 overflow-hidden"
          >
            <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-2">
              {vehicles.map(v => (
                <button
                  key={v.id}
                  onClick={() => toggleVehicle(v.id)}
                  className={`flex items-center justify-between p-2 rounded-lg text-sm transition-all ${
                    selectedIds.includes(v.id)
                      ? 'bg-audi-red text-white'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  <span>{v.year} {v.model}</span>
                  {selectedIds.includes(v.id) && <SafeIcon icon={FiCheck} className="w-3 h-3" />}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Comparison Grid */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr>
              <th className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-1/4">Features</th>
              {selectedVehicles.map(v => (
                <th key={v.id} className="p-4 min-w-[150px]">
                  <div className="relative group">
                    <img src={v.image} alt={v.model} className="w-full h-24 object-cover rounded-lg mb-2 border border-gray-700" />
                    <button
                      onClick={() => toggleVehicle(v.id)}
                      className="absolute -top-2 -right-2 bg-gray-900 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <SafeIcon icon={FiX} className="w-3 h-3" />
                    </button>
                    <div className="font-bold text-white text-sm">{v.year} {v.model}</div>
                    <div className="text-xs text-gray-400">{v.trim}</div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {comparisonFeatures.map((feature, idx) => (
              <tr key={idx} className="hover:bg-gray-700/30 transition-colors">
                <td className="p-4 flex items-center space-x-2">
                  <SafeIcon icon={feature.icon} className="w-4 h-4 text-audi-red" />
                  <span className="text-sm font-medium text-gray-300">{feature.label}</span>
                </td>
                {selectedVehicles.map(v => (
                  <td key={v.id} className="p-4 text-sm text-white font-semibold">
                    {feature.format(v[feature.key])}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td className="p-4 text-sm font-medium text-gray-300">Action</td>
              {selectedVehicles.map(v => (
                <td key={v.id} className="p-4">
                  <button className="w-full bg-audi-red hover:bg-red-700 text-white py-1.5 rounded text-xs font-bold transition-colors">
                    View Details
                  </button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      
      <div className="p-4 bg-gray-900/30 text-center">
        <p className="text-xs text-gray-500 italic">
          Specifications are based on current inventory data. Prices exclude taxes and fees.
        </p>
      </div>
    </motion.div>
  );
};

export default ModelComparison;